import React, { useEffect, useState } from "react";
import { UserPlus, Search, Phone, Briefcase, Eye, Calendar } from "lucide-react";
import SummaryApi from "../common";
import EmployeeForm from "./EmployeeForm";
import AttendanceModal from "./AttendanceModal";
import { useNavigate } from "react-router-dom";
import fetchWithAuth from "../common/fetchWithAuth";

export default function EmployeeList() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [pages, setPages] = useState(1);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [showAttendance, setShowAttendance] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const navigate = useNavigate();

    const fetchStaff = async (p = 1, q = "") => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ page: p, limit });
            if (q) params.set("q", q);
            const url = `${SummaryApi.getAllStaff.url}?${params.toString()}`;
            const res = await fetchWithAuth(url, {
                method: SummaryApi.getAllStaff.method,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to load");
            setStaff(data.data || []);
            setPages(data.meta?.pages || 1);
            setError("");
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load staff");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Only fetch on page change if it's not triggered by search reset
        if (page !== 1 || search === "") {
            fetchStaff(page, search);
        }
    }, [page]);

    const onCreate = () => {
        setShowForm(true);
    };

    const onFormSuccess = (saved) => {
        setShowForm(false);
        fetchStaff(page, search);
    };



    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (page === 1) {
                fetchStaff(1, search);
            } else {
                setPage(1);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
                    <input
                        placeholder="Search staff by name or number..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                    />
                </div>
                <div className="flex gap-3">
                    <button
                        className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl font-medium transition-all shadow-sm"
                        onClick={() => navigate('/dashboard/salary')}
                    >
                        <Briefcase size={20} className="text-gray-500" />
                        Salary Management
                    </button>
                    <button
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl font-medium transition-all shadow-lg shadow-emerald-200"
                        onClick={onCreate}
                    >
                        <UserPlus size={20} />
                        Add Employee
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="mb-8 bg-gray-50 p-6 rounded-2xl border border-gray-200 animate-in fade-in slide-in-from-top-4">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">New Employee</h3>
                    <EmployeeForm
                        initialData={null}
                        onSuccess={onFormSuccess}
                        onCancel={() => setShowForm(false)}
                    />
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Designation</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Joining Date</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {staff.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                                        No employees found. Add one to get started.
                                    </td>
                                </tr>
                            )}
                            {staff.map((s) => (
                                <tr key={s._id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                                                {s.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="font-medium text-gray-900">{s.name}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <Phone size={14} className="text-gray-400" />
                                            {s.phone || "-"}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <Briefcase size={14} className="text-gray-400" />
                                            {s.designation || "-"}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {s.joiningDate
                                            ? new Date(s.joiningDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                            : "-"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                title="View Details"
                                                className="flex items-center gap-2 px-3 py-1.5 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors text-sm font-medium"
                                                onClick={() => navigate(`/dashboard/employees/${s._id}`, { state: { employee: s } })}
                                            >
                                                <Eye size={16} />
                                                View
                                            </button>
                                            <button
                                                title="Mark Attendance"
                                                className="flex items-center gap-2 px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium"
                                                onClick={() => {
                                                    setSelectedStaff(s);
                                                    setShowAttendance(true);
                                                }}
                                            >
                                                <Calendar size={16} />
                                                Attendance
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <div className="text-sm text-gray-500">
                            Page {page} of {pages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                disabled={page === 1}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                                className="px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                disabled={page === pages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAttendance && selectedStaff && (
                <AttendanceModal
                    staff={selectedStaff}
                    onClose={() => {
                        setShowAttendance(false);
                        setSelectedStaff(null);
                    }}

                />
            )}



            {error && <div className="text-rose-600 mt-4 bg-rose-50 p-3 rounded-lg border border-rose-100 text-sm">{error}</div>}
        </div>
    );
}